import { useState, useEffect } from 'react';
import { getRevenueDeepDiveAnalytics } from '../../services/revenueDeepDiveService';
import { getSubscriptionLifecycleEvents } from '../../services/analyticsSubscriptionEventsService';
import BarChart from '../charts/BarChart';
import PieChart from '../charts/PieChart';
import LineChart from '../charts/LineChart';

const RevenueDeepDiveTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [revenueData, setRevenueData] = useState<any>(null);
  const [lifecycleEvents, setLifecycleEvents] = useState<Awaited<ReturnType<typeof getSubscriptionLifecycleEvents>> | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [data, lifecycle] = await Promise.all([
          getRevenueDeepDiveAnalytics(90),
          getSubscriptionLifecycleEvents(90).catch(() => null),
        ]);
        setRevenueData(data);
        setLifecycleEvents(lifecycle);
      } catch (error) {
        console.error('Error loading revenue deep dive data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading revenue analytics...</p>
      </div>
    );
  }

  if (!revenueData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No revenue data available</p>
      </div>
    );
  }

  // Revenue by plan type chart
  const revenueByPlanData = {
    labels: revenueData.revenueByPlanType.map((p: any) => p.planId),
    datasets: [
      {
        label: 'Revenue',
        data: revenueData.revenueByPlanType.map((p: any) => p.revenue),
        backgroundColor: ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'],
      },
    ],
  };

  // Revenue by cohort chart
  const revenueByCohortData = {
    labels: revenueData.revenueByCohort.map((c: any) => c.cohort),
    datasets: [
      {
        label: 'Revenue',
        data: revenueData.revenueByCohort.map((c: any) => c.revenue),
        borderColor: '#2563eb',
        backgroundColor: 'rgba(37, 99, 235, 0.1)',
        fill: true,
      },
    ],
  };

  // Payment failure reasons
  const failureKeys = Array.from(revenueData.paymentFailureMetrics.failureReasons.keys()).slice(0, 10);
  const failureValues = Array.from(revenueData.paymentFailureMetrics.failureReasons.values()).slice(0, 10);
  const failureReasonsData = {
    labels: failureKeys as string[],
    datasets: [
      {
        label: 'Count',
        data: failureValues as number[],
        backgroundColor: '#ef4444',
      },
    ],
  };

  // LTV by cohort
  const ltvKeys = Array.from(revenueData.ltvByCohort.keys());
  const ltvValues = Array.from(revenueData.ltvByCohort.values());
  const ltvByCohortData = {
    labels: ltvKeys as string[],
    datasets: [
      {
        label: 'Lifetime Value',
        data: ltvValues as number[],
        backgroundColor: '#10b981',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Revenue Deep Dive
      </h1>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Payment Recovery Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {revenueData.paymentFailureMetrics.recoveryRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Trial Conversion Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {revenueData.subscriptionLifecycle.trialConversionRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Cancellation Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {revenueData.subscriptionLifecycle.cancellationRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg Subscription Duration</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {revenueData.subscriptionLifecycle.averageDuration.toFixed(0)} days
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Refund Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {revenueData.refundRate.toFixed(2)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Total Refunds</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            ${revenueData.totalRefunds.toFixed(2)}
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Revenue by Plan Type</h3>
          <PieChart data={revenueByPlanData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Revenue Trends by Cohort</h3>
          <LineChart data={revenueByCohortData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Payment Failure Reasons</h3>
          <BarChart data={failureReasonsData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Lifetime Value by Cohort</h3>
          <BarChart data={ltvByCohortData} height={300} />
        </div>
      </div>

      {/* Revenue by Plan Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
        marginBottom: '2rem',
      }}>
        <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
          Revenue by Plan Type
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Plan</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Revenue</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Subscriptions</th>
              <th style={{ padding: '1rem', textAlign: 'left' }}>Avg per Subscription</th>
            </tr>
          </thead>
          <tbody>
            {revenueData.revenueByPlanType.map((plan: any, index: number) => (
              <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '1rem' }}>{plan.planId}</td>
                <td style={{ padding: '1rem' }}>${plan.revenue.toFixed(2)}</td>
                <td style={{ padding: '1rem' }}>{plan.count}</td>
                <td style={{ padding: '1rem' }}>
                  ${plan.count > 0 ? (plan.revenue / plan.count).toFixed(2) : '0.00'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Subscription lifecycle events (Stripe + mobile) */}
      {lifecycleEvents !== null && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginTop: '2rem',
        }}>
          <h2 style={{ marginTop: 0, marginBottom: '1rem' }}>Subscription lifecycle events</h2>
          <p style={{ color: '#666', fontSize: '0.875rem', marginBottom: '1rem' }}>
            From analytics_subscription_events (Stripe webhook + mobile verification). Last 90 days.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
            <div><strong>Events (7d):</strong> {lifecycleEvents.last7dCount}</div>
            <div><strong>Events (30d):</strong> {lifecycleEvents.last30dCount}</div>
          </div>
          {lifecycleEvents.events.length === 0 && Object.keys(lifecycleEvents.byEventType).length === 0 && (
            <p style={{ color: '#666', fontSize: '0.875rem' }}>No subscription lifecycle events in the last 90 days.</p>
          )}
          {Object.keys(lifecycleEvents.byEventType).length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>By event type</h3>
              <BarChart
                data={{
                  labels: Object.keys(lifecycleEvents.byEventType),
                  datasets: [{
                    label: 'Count',
                    data: Object.values(lifecycleEvents.byEventType),
                    backgroundColor: '#2563eb',
                  }],
                }}
                height={280}
              />
            </div>
          )}
          {lifecycleEvents.eventsOverTime.length > 0 && (
            <div>
              <h3 style={{ marginBottom: '0.5rem' }}>Events over time</h3>
              <LineChart
                data={{
                  labels: lifecycleEvents.eventsOverTime.map((d) => d.date),
                  datasets: [{
                    label: 'Events',
                    data: lifecycleEvents.eventsOverTime.map((d) => d.count),
                    borderColor: '#10b981',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  }],
                }}
                height={260}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RevenueDeepDiveTab;

